/* ═══════════════════════════════════════════════════════════════
   The Master Forma Studio — the intentionally-blank page, previewed.

   The classic editor's <BlankPage> (CanvasComponents.tsx) is a drag-to-
   position preview built on that canvas's own fixed PPI*scale constant
   (utils.ts's `inPx`/`ptPx`, baked to a 0.82 display scale) — mixing it
   with the Studio's real body-layout brain (`inToPx`/`ptToPx`, true
   96dpi) would make the blank page a visibly different size than the
   real page beside it. WYSIWYG IS LAW, so this is a small re-render
   through the SAME brain the rest of the Studio's paper uses, per the
   contract's own fallback ("otherwise re-render via shared brains").
   Editing (justify / vertical position / text) lives in the Section End
   panel — this is the read view that proves it prints as shown.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { StandardConfig } from '../../shared/types';
import { inToPx, ptToPx, resolvePageGeometry } from '../../shared/body-layout';

export function BlankPagePreview({ forma }: { forma: StandardConfig }) {
  const sp = forma.special || ({} as StandardConfig['special']);
  const geo = resolvePageGeometry(forma);
  const pW = inToPx(geo.pageWidthIn);
  const pH = inToPx(geo.pageHeightIn);
  const mT = inToPx(geo.marginTopIn);
  const mB = inToPx(geo.marginBottomIn);
  const mL = inToPx(geo.marginLeftIn);
  const mR = inToPx(geo.marginRightIn);
  const contentH = pH - mT - mB;
  const vertPct = sp.blankPageVertical ?? 50;
  const textY = mT + contentH * (vertPct / 100);
  const justify = sp.blankPageJustify || 'center';

  return (
    <div
      className="sfs-blankpage"
      style={{ width: pW, height: pH }}
      role="img"
      aria-label="Preview of the intentionally-blank page this forma prints"
    >
      <span className="sfs-blankpage-tag">BLANK PAGE</span>
      <div
        className="sfs-blankpage-text"
        style={{
          top: textY,
          left: mL,
          right: mR,
          textAlign: justify as React.CSSProperties['textAlign'],
          fontFamily: geo.bodyFont,
          fontSize: ptToPx(geo.bodyFontSizePt),
        }}
      >
        {sp.intentionallyBlankText || 'THIS PAGE INTENTIONALLY LEFT BLANK'}
      </div>
    </div>
  );
}
